import React, { useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { ArrowLeft, Download, Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, XCircle, Info, Image as ImageIcon, Database } from "lucide-react";

// -----------------------------------------------------------------------------
// ImportExport — /admin/importazione
// Guided 4-step CSV/XLSX bulk import of businesses + one-click export.
// -----------------------------------------------------------------------------

const Step = ({ n, title, active, done }) => (
  <div className="flex items-center gap-3">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${done ? "bg-[#12964A] text-white" : active ? "bg-[#F0FAF3] text-[#12964A] border-2 border-[#12964A]" : "bg-slate-100 text-slate-400"}`}>{done ? "✓" : n}</div>
    <div className={`text-sm ${active || done ? "text-[#1F2937] font-medium" : "text-slate-400"}`}>{title}</div>
  </div>
);

const ImportExport = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);
  const [conflict, setConflict] = useState("skip");
  const [error, setError] = useState(null);

  const step = result ? 4 : preview ? 3 : file ? 2 : 1;

  const doPreview = async () => {
    if (!file) return;
    setPreviewing(true); setError(null); setResult(null);
    try {
      const r = await adminApi.importPreview(file, conflict);
      setPreview(r.data);
    } catch (e) { setError(e?.response?.data?.detail || "Errore anteprima"); }
    finally { setPreviewing(false); }
  };

  const doCommit = async () => {
    if (!file || !preview) return;
    setCommitting(true); setError(null);
    try {
      const r = await adminApi.importCommit(file, conflict);
      setResult(r.data);
    } catch (e) { setError(e?.response?.data?.detail || "Errore importazione"); }
    finally { setCommitting(false); }
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); setError(null); };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#12964A]"><ArrowLeft className="w-4 h-4" /> Pannello</Link>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Amministrazione</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-[#1F2937]">Importa / Esporta attività</h1>
          <p className="text-slate-500 mt-2 max-w-2xl">Carica un file Excel o CSV per importare più attività contemporaneamente. Oppure esporta tutte le attività in un unico file.</p>
        </div>

        {/* Export */}
        <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Esportazione</div>
              <div className="font-display font-bold text-xl text-[#1F2937]">Scarica tutte le attività</div>
              <div className="text-sm text-slate-500 mt-1">File compatibile con il template di importazione.</div>
            </div>
            <div className="flex gap-2">
              <a href={adminApi.exportUrl("xlsx", true)} className="inline-flex items-center gap-2 border border-[#E5E7EB] hover:border-[#12964A] rounded-full px-5 py-2.5 text-sm bg-white" data-testid="export-xlsx"><Download className="w-4 h-4" /> Excel (.xlsx)</a>
              <a href={adminApi.exportUrl("csv", true)} className="inline-flex items-center gap-2 border border-[#E5E7EB] hover:border-[#12964A] rounded-full px-5 py-2.5 text-sm bg-white" data-testid="export-csv"><Download className="w-4 h-4" /> CSV</a>
            </div>
          </div>
        </section>

        {/* Bulk image import via ZIP */}
        <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Immagini</div>
              <div className="font-display font-bold text-xl text-[#1F2937]">Importa immagini da ZIP</div>
              <div className="text-sm text-slate-500 mt-1">Carica un ZIP di foto: verranno abbinate automaticamente allo slug dell&apos;attività.</div>
            </div>
            <Link to="/admin/importazione/immagini" className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-5 py-2.5 text-sm" data-testid="link-import-images">
              <ImageIcon className="w-4 h-4" /> Apri importazione immagini
            </Link>
          </div>
        </section>

        {/* WordPress JSON migration */}
        <section className="bg-gradient-to-br from-[#F0FAF3] to-white border border-emerald-200 rounded-3xl p-6 md:p-8">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.22em] text-emerald-700 mb-1">Migrazione WordPress</div>
              <div className="font-display font-bold text-xl text-[#1F2937]">Importa JSON dal vecchio sito WP</div>
              <div className="text-sm text-slate-600 mt-1 max-w-2xl">
                Modulo dedicato ai 70 record esportati con <code className="font-mono text-xs">wp_exporter.php</code>.
                Include Preview + Diff, download automatico dei media, dedup SHA-256, idempotenza e protezione delle
                modifiche manuali (conflict resolution).
              </div>
            </div>
            <Link to="/admin/migrazione-json" className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-5 py-2.5 text-sm" data-testid="link-wp-migration">
              <Database className="w-4 h-4" /> Apri migrazione JSON
            </Link>
          </div>
        </section>

        {/* Import wizard */}
        <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Importazione guidata</div>
          <div className="font-display font-bold text-2xl text-[#1F2937] mb-6">4 semplici passi</div>

          <div className="flex flex-wrap items-center gap-2 md:gap-4 mb-8 pb-6 border-b border-[#F1F5F9]">
            <Step n={1} title="Scarica template" active={step === 1} done={step > 1} />
            <div className="hidden md:block w-8 h-px bg-slate-200" />
            <Step n={2} title="Carica file" active={step === 2} done={step > 2} />
            <div className="hidden md:block w-8 h-px bg-slate-200" />
            <Step n={3} title="Anteprima" active={step === 3} done={step > 3} />
            <div className="hidden md:block w-8 h-px bg-slate-200" />
            <Step n={4} title="Riepilogo" active={step === 4} done={step > 4} />
          </div>

          {/* STEP 1 */}
          {!file && (
            <div>
              <div className="rounded-2xl bg-[#F0FAF3] border border-[#E1F3E8] p-5 flex items-start gap-3 mb-6">
                <Info className="w-5 h-5 text-[#12964A] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700">
                  <strong>Consiglio:</strong> scarica prima il template, compilalo con Excel o LibreOffice, poi caricalo qui. Le istruzioni per ogni colonna sono nel foglio &quot;Istruzioni&quot; del template Excel.
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mb-6">
                <a href={adminApi.importTemplateUrl("xlsx")} className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-5 py-2.5 text-sm" data-testid="template-xlsx">
                  <FileSpreadsheet className="w-4 h-4" /> Scarica template Excel
                </a>
                <a href={adminApi.importTemplateUrl("csv")} className="inline-flex items-center gap-2 border border-[#E5E7EB] hover:border-[#12964A] rounded-full px-5 py-2.5 text-sm bg-white">
                  <Download className="w-4 h-4" /> Template CSV
                </a>
              </div>
              <label className="block border-2 border-dashed border-[#E5E7EB] hover:border-[#12964A] rounded-2xl p-10 text-center cursor-pointer transition-colors">
                <Upload className="w-8 h-8 text-slate-400 mx-auto mb-3" />
                <div className="text-sm text-slate-600">Trascina qui il file oppure <span className="text-[#12964A] font-medium">clicca per selezionare</span></div>
                <div className="text-xs text-slate-400 mt-2">Formati supportati: .xlsx, .csv</div>
                <input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} data-testid="import-file" />
              </label>
            </div>
          )}

          {/* STEP 2 — file selected, not yet previewed */}
          {file && !preview && !result && (
            <div>
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl mb-6">
                <FileSpreadsheet className="w-6 h-6 text-[#12964A]" />
                <div className="flex-1"><div className="font-medium text-[#1F2937]">{file.name}</div><div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div></div>
                <button onClick={reset} className="text-xs text-red-600 hover:underline">Cambia file</button>
              </div>
              <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-2">Se un&apos;attività esiste già (stesso slug)</div>
                <div className="flex gap-2">
                  <button onClick={() => setConflict("skip")} className={`px-4 py-2 rounded-full text-sm border ${conflict === "skip" ? "bg-[#12964A] text-white border-[#12964A]" : "bg-white border-[#E5E7EB]"}`}>Salta (default)</button>
                  <button onClick={() => setConflict("update")} className={`px-4 py-2 rounded-full text-sm border ${conflict === "update" ? "bg-[#12964A] text-white border-[#12964A]" : "bg-white border-[#E5E7EB]"}`}>Aggiorna la scheda esistente</button>
                </div>
              </div>
              <button onClick={doPreview} disabled={previewing} className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-50 text-white rounded-full px-6 py-3 text-sm" data-testid="import-preview">
                {previewing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analisi in corso…</> : <>Analizza file →</>}
              </button>
            </div>
          )}

          {/* STEP 3 — preview */}
          {preview && !result && (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat n={preview.total} label="Righe totali" />
                <Stat n={preview.valid} label="Valide" color="green" />
                <Stat n={preview.warnings} label="Avvisi" color="yellow" />
                <Stat n={preview.errors} label="Errori" color="red" />
              </div>
              <div className="max-h-[400px] overflow-y-auto border border-[#F1F5F9] rounded-2xl mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0"><tr>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">#</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Attività</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Azione</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">Note</th>
                  </tr></thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.line} className="border-t border-[#F1F5F9]">
                        <td className="px-4 py-2 text-slate-500">{r.line}</td>
                        <td className="px-4 py-2 flex items-center gap-2">
                          {r.status === "error" && <XCircle className="w-4 h-4 text-red-500" />}
                          {r.status === "warning" && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                          {r.status === "ok" && <CheckCircle2 className="w-4 h-4 text-[#12964A]" />}
                          <span>{r.name || <em className="text-slate-400">senza nome</em>}</span>
                        </td>
                        <td className="px-4 py-2"><ActionBadge action={r.action} /></td>
                        <td className="px-4 py-2 text-slate-600">{r.messages?.join(" · ") || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <button onClick={reset} className="text-sm text-slate-500 hover:text-slate-700">← Ricarica file</button>
                <div className="flex-1" />
                <button onClick={doCommit} disabled={committing || preview.valid === 0} className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-50 text-white rounded-full px-6 py-3 text-sm" data-testid="import-commit">
                  {committing ? <><Loader2 className="w-4 h-4 animate-spin" /> Importazione in corso…</> : <>Conferma importazione ({preview.valid} righe) →</>}
                </button>
              </div>
            </div>
          )}

          {/* STEP 4 — result */}
          {result && (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat n={result.created} label="Create" color="green" />
                <Stat n={result.updated} label="Aggiornate" color="blue" />
                <Stat n={result.skipped} label="Saltate" color="yellow" />
                <Stat n={result.errors} label="Errori" color="red" />
              </div>
              <div className="rounded-2xl bg-[#F0FAF3] border border-[#E1F3E8] p-5 mb-6">
                <div className="flex items-center gap-2 text-[#0E7C3D] font-medium mb-1"><CheckCircle2 className="w-5 h-5" /> Importazione completata</div>
                <p className="text-sm text-slate-600">Le nuove attività sono ora disponibili nel pannello. Puoi rivederle e pubblicarle dal <Link to="/admin" className="text-[#12964A] underline">pannello attività</Link>.</p>
              </div>
              <button onClick={reset} className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-5 py-2 text-sm hover:border-[#12964A]">Nuova importazione</button>
            </div>
          )}

          {error && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}
        </section>
      </main>
    </div>
  );
};

const Stat = ({ n, label, color = "slate" }) => {
  const colors = { slate: "bg-slate-50 text-slate-700", green: "bg-[#F0FAF3] text-[#0E7C3D]", yellow: "bg-amber-50 text-amber-700", red: "bg-red-50 text-red-700", blue: "bg-blue-50 text-blue-700" };
  return (
    <div className={`rounded-2xl p-4 ${colors[color] || colors.slate}`}>
      <div className="text-2xl font-bold">{n}</div>
      <div className="text-xs uppercase tracking-[0.15em] mt-1">{label}</div>
    </div>
  );
};

const ActionBadge = ({ action }) => {
  if (!action) return null;
  const map = { create: ["Creazione", "bg-[#F0FAF3] text-[#0E7C3D]"], update: ["Aggiornamento", "bg-blue-50 text-blue-700"], skip: ["Salta", "bg-amber-50 text-amber-700"] };
  const [label, cls] = map[action] || [action, "bg-slate-100 text-slate-600"];
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${cls}`}>{label}</span>;
};

export default ImportExport;
